import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import moment from "moment";
import DropdownController from "../../../components/inputs/DropdownController";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import { isValidDateTest, maxLengthMessage } from "../../../utils/functions";
import DatePickerController from "../../../components/inputs/DatePickerController";

// Validation schema
const assignmentHistorySchema = yup.object().shape({
  effectiveDate: isValidDateTest("Effective Date").required("Effective Date is required").nonNullable(),
  department: yup.string().max(30, maxLengthMessage(30)).nullable(),
  departmentDate: isValidDateTest("Department/Division Date"),
  titlePosition: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
  rank: yup.string().trim().max(20, maxLengthMessage(20)).nullable(),
  section: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
  sectionDate: isValidDateTest("Section Date"),
});

const AssignmentHistoryDialog = ({
  open,
  onClose,
  onSave,
  initialData,
  departmentOptions,
  titlePositionOptions,
  rankOptions,
  sectionOptions
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(assignmentHistorySchema),
    defaultValues: {
      effectiveDate: initialData?.effectiveDate || "",
      department: initialData?.department || "",
      departmentDate: initialData?.departmentDate || "",
      titlePosition: initialData?.titlePosition || "",
      rank: initialData?.rank || "",
      section: initialData?.section || "",
      sectionDate: initialData?.sectionDate || "",
    },
  });

  useEffect(() => {
    reset({
      effectiveDate: initialData?.effectiveDate
        ? moment(initialData.effectiveDate).format("MM/DD/YYYY")
        : "",
      department: initialData?.department || "",
      departmentDate: initialData?.departmentDate
        ? moment(initialData.departmentDate).format("MM/DD/YYYY")
        : "",
      titlePosition: initialData?.titlePosition || "",
      rank: initialData?.rank || "",
      section: initialData?.section || "",
      sectionDate: initialData?.sectionDate
        ? moment(initialData.sectionDate).format("MM/DD/YYYY")
        : "",
    });
  }, [initialData, reset]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = (data) => {
    const assignmentData = {
      effectiveDate: data.effectiveDate
        ? moment(data.effectiveDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      department: data.department.trim() || null,
      departmentDate: data.departmentDate
        ? moment(data.departmentDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      titlePosition: data.titlePosition || null,
      rank: data.rank || null,
      section: data.section.trim() || null,
      sectionDate: data.sectionDate
        ? moment(data.sectionDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
    };
    onSave(assignmentData);
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initialData ? "Edit Assignment History" : "Add Assignment History"}
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ mt: 1 }}
        >
          <Box sx={{ mb: 2 }}>
            <DatePickerController
              name="effectiveDate"
              label="Effective Date"
              control={control}
              errors={errors}
              required
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <DropdownController
              name="department"
              label="Department/Division"
              options={departmentOptions}
              control={control}
              errors={errors}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <DatePickerController
              name="departmentDate"
              label="Department/Division Date"
              control={control}
              errors={errors}
              required
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <DropdownController
              name="titlePosition"
              label="Title/Position"
              options={titlePositionOptions}
              control={control}
              errors={errors}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <DropdownController
              name="rank"
              label="Rank"
              options={rankOptions}
              control={control}
              errors={errors}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <DropdownController
              name="section"
              label="Section"
              options={sectionOptions}
              control={control}
              errors={errors}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <DatePickerController
              name="sectionDate"
              label="Section Assigned Date"
              control={control}
              errors={errors}
              required
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <PATSaveButton onClick={handleSubmit(onSubmit)} tooltipText="Save" />
        <PATCloseButton onClick={onClose} tooltipText="Close" />
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentHistoryDialog;
